import React, { Fragment } from 'react';
import PropTypes from 'prop-types';

import { styled } from '@mui/material/styles';

// Components
import {
  Avatar,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  Typography,
  useMediaQuery,
} from '@mui/material';
import ThumbUpRoundedIcon from '@mui/icons-material/ThumbUpRounded';
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import RepeatRoundedIcon from '@mui/icons-material/RepeatRounded';
import AccountBoxRoundedIcon from '@mui/icons-material/AccountBoxRounded';
import CropRotateRoundedIcon from '@mui/icons-material/CropRotateRounded';

const FailedIcon = styled(RepeatRoundedIcon)(({ theme }) => ({
  color: theme.palette.error.main,
  fontSize: 30,
}));

const ReceivedIcon = styled(RepeatRoundedIcon)(({ theme }) => ({
  color: theme.palette.success.main,
  fontSize: 30,
}));

const RejectedIcon = styled(RepeatRoundedIcon)(({ theme }) => ({
  color: theme.palette.warning.dark,
  fontSize: 30,
}));

const StyledAccountBoxIcon = styled(AccountBoxRoundedIcon)(({ theme }) => ({
  color: theme.palette.warning.dark,
  fontSize: 30,
}));

const StyledCropRotateIcon = styled(CropRotateRoundedIcon)(({ theme }) => ({
  color: theme.palette.warning.dark,
  fontSize: 30,
}));

// determines avatar color from notificationType prop and theme
const getAvatarColor = notificationType => {
  switch (notificationType) {
    case 'success':
      return 'success.light';
    case 'warning':
      return 'error.light';
    default:
      return 'warning.light';
  }
};

const MNotificationSection = ({ handleMobileClose, notificationList, onClick }) => {
  const smDown = useMediaQuery(theme => theme.breakpoints.down('sm'), { noSsr: true });
  const lgDown = useMediaQuery(theme => theme.breakpoints.down('lg'), { noSsr: true });

  const notificationItem = singleNotification => {
    const {
      dataEntityId,
      notificationData,
      notificationType,
      notificationMessage,
      propName,
      shortName,
    } = singleNotification;
    const handleSelect = () => {
      onClick(
        // notificationData may be null or undefined, so need to use nullish coallescing here
        {
          affiliateId: notificationData?.supplierIdName,
          dataCompanyId: notificationData?.dataCompanyId,
          product: notificationData?.productName,
          [propName]: dataEntityId,
          shortName,
        },
        notificationType
      );
      handleMobileClose();
    };

    const getNotificationItem = (IconComponent = ReceivedIcon) => {
      return (
        <ListItemButton className="heap-notification" onClick={handleSelect}>
          <ListItemIcon>
            <Avatar
              sx={{
                backgroundColor: getAvatarColor(notificationType),
              }}
            >
              <IconComponent />
            </Avatar>
          </ListItemIcon>
          <Typography variant="body2">{notificationMessage}</Typography>
        </ListItemButton>
      );
    };

    switch (notificationType) {
      case 'success':
        return getNotificationItem(ReceivedIcon);
      case 'alert':
        return getNotificationItem(RejectedIcon);
      case 'warning':
        return getNotificationItem(FailedIcon);
      case 'expire_warning':
        return getNotificationItem(StyledAccountBoxIcon);
      case 'two_party_warning':
        return getNotificationItem(StyledCropRotateIcon);
      default:
        return (
          <ListItem
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
            }}
          >
            <ThumbUpRoundedIcon
              sx={{
                color: 'graphic.main',
                fontSize: 57,
              }}
            />
            <Typography
              sx={{
                display: 'flex',
                justifyContent: 'center',
                paddingLeft: 1.5,
              }}
              variant="body1"
            >
              You have 0 notifications
            </Typography>
          </ListItem>
        );
    }
  };

  return (
    <List
      sx={{
        // eslint-disable-next-line sort-keys-fix/sort-keys-fix
        maxWidth: { xs: 'revert', sm: 375 },
        mb: 3,
      }}
    >
      <ListItem variant="h6">
        {smDown ? (
          <Typography
            sx={{
              display: 'flex',
              justifyContent: 'center',
              paddingLeft: 1.5,
            }}
            variant="h6"
          >
            Notifications
          </Typography>
        ) : (
          <>
            <ChevronLeftRoundedIcon />
            <Typography
              sx={{
                display: 'flex',
                justifyContent: 'center',
                paddingLeft: 1.5,
              }}
              variant="h6"
            >
              Notifications
            </Typography>
          </>
        )}
        {lgDown && (
          <IconButton
            onClick={handleMobileClose}
            size="large"
            sx={{
              color: 'secondary.medium',
              marginLeft: 'auto',
              marginRight: 1,
            }}
          >
            <CloseRoundedIcon />
          </IconButton>
        )}
      </ListItem>

      <Divider />

      {!notificationList.length ? (
        <ListItem
          key="no_notifications"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
          }}
        >
          <ThumbUpRoundedIcon
            sx={{
              color: 'graphic.main',
              fontSize: 57,
            }}
          />
          <Typography
            sx={{
              display: 'flex',
              justifyContent: 'center',
              paddingLeft: 1.5,
            }}
            variant="body1"
          >
            You have 0 notifications
          </Typography>
        </ListItem>
      ) : (
        notificationList.map(singleNotification => (
          <Fragment key={singleNotification.dataEntityId}>
            {notificationItem(singleNotification)}
            <Divider />
          </Fragment>
        ))
      )}
    </List>
  );
};

MNotificationSection.propTypes = {
  error: PropTypes.shape({}),
  // handleMobileClose will not be used in desktop, not required
  handleMobileClose: PropTypes.func,
  notificationList: PropTypes.arrayOf(PropTypes.shape({})),
  onClick: PropTypes.func.isRequired,
};
MNotificationSection.defaultProps = {
  error: null,
  handleMobileClose: () => {},
  notificationList: [],
};

export default MNotificationSection;
